import './CreateTimelineModal.scss';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import Rodal from '../../../../components/Rodal/Rodal';
import * as yup from 'yup';
import { Form, Field, Input } from '@availity/form';
import createNotification from '../../../../utils/createNotification';
import Loader from 'react-loaders';
import {
    Col,
    FormGroup,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Label,
    ModalBody,
    ModalHeader,
    Row,
} from 'reactstrap';
import CustomAutocomplete from '../../../../components/CustomAutocomplete/CustomAutocomplete';
import { useDispatch } from 'react-redux';
import { GetTimelines } from '../../../../slices/marketplace/timeline/thunks/get-timeline-templates.thunk';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { ArrowDown } from '../../../../components/arrow-down/arrow-down';
import { Multiselect } from 'react-widgets/cjs';
import { LegalEntityTypeList } from '../../../../slices/marketplace/timeline/models/enums/legal-enity-type';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import {
    TimelineTempo,
    TimelineTempos,
} from '../../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import { LevelOfServiceOptions } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import StateSelectField from '../../../../components/FormFields/StateSelectField/StateSelectField';

export interface CreateTimelineModalProps {
    isVisible: boolean;
    onClose: (id: number | undefined) => void;
}

interface CreateTimelineFormValues {
    timelineTemplate: number | undefined;
    practice: number;
    engagementLetter: string;
    tempo: TimelineTempo;
}

interface DropdownOption {
    id: number;
    name: string;
}

export const CreateTimelineModal = (props: CreateTimelineModalProps) => {
    const modalRef = useRef(null);
    const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => (
        <div ref={ref} className={'btn'} onClick={onClick}>
            {value}
        </div>
    ));
    const timelineTemplatesTable = useStateSelector(
        (state) => state.timelines.timelineTemplatesTable
    );
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);
    const [isLoading, setIsLoading] = useState(false);
    const [practiceValidationErrors, setPracticeValidationErrors] = useState<
        Record<number, boolean>
    >({});

    const [userOptions, setUserOptions] = useState<DropdownOption[]>([]);
    const [selectedUser, setSelectedUser] = useState<DropdownOption | null>();
    const [isUserFieldTouched, setIsUserFieldTouched] = useState(false);

    const [practices, setPractices] = useState<Array<DropdownOption>>([]);
    const [practicesAreloading, setPracticesAreLoading] = useState(false);
    const [isPracticeMultiselectError, setIsPracticeMultiselectError] =
        useState(false);

    const [selectedPracticeIds, setSelectedPracticeIds] =
        useState<number[]>(null);
    const [isPracticeListOpened, setIsPracticeListOpened] = useState(false);

    const [engagementLetterFile, setEngagementLetterFile] = useState(null);
    const fileSelectorExtensions = '.pdf';
    const [selectedPractices, setSelectedPractices] = useState<
        Array<{
            practiceId: number;
            stateOfRegistration: string;
            entityType: string;
            entityName: string;
        }>
    >([]);

    const [eventDateStart, setEventDateStart] = useState<Date>(new Date());
    const StartDateRef = useRef(null);
    const [activeTab, setActiveTab] = useState<number>(0);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        initialize().then();
    }, []);

    useEffect(() => {
        setPractices([]);
        if (selectedUser?.id) {
            setPracticesAreLoading(true);
            axios
                .get(
                    `api/Practice/GetUserPractices?userId=${selectedUser.id}&includeOnlyCurrentPractices=true`
                )
                .then((response: any) => {
                    if (response) {
                        setPractices(response.data.data);
                    }
                })
                .catch(() => {
                    createNotification(
                        'An unexpected error occurred while fetching practices.',
                        'error'
                    );
                })
                .finally(() => {
                    setPracticesAreLoading(false);
                });
        }
    }, [selectedUser]);

    const initialize = async () => {
        setIsLoading(true);
        await fetchUsers();
        await dispatch(
            GetTimelines({
                pageNumber: 1,
                pageSize: 10000,
                filter: {
                    filters: [],
                    logic: '',
                },
                sort: {
                    dir: '',
                    field: '',
                },
            })
        );
        setIsLoading(false);
    };

    const fetchUsers = async () => {
        let response = await axios.get('api/Users/practiceOwners').catch(() => {
            createNotification(
                'An unexpected error occurred while fetching users.',
                'error'
            );
        });
        if (response) {
            const usersList = response.data.map((user: any) => ({
                id: user.id,
                name: `${user.name} ${user.email}`,
            }));
            setUserOptions(usersList);
        }
    };

    const changeFileHandler = (event: any) => {
        setEngagementLetterFile(event.target.files[0]);
        modalRef.current.setFieldValue(
            'engagementLetter',
            event.target.files[0]?.name ?? '',
            true
        );
    };

    const createEngagement = (values: CreateTimelineFormValues) => {
        setIsSaving(true);

        const practicesTrimmedData = selectedPractices.map((practice) => ({
            ...practice,
            entityName: practice.entityName.trim(),
        }));

        const formData = new FormData();
        formData.append('File', engagementLetterFile);
        formData.append(
            'Data',
            JSON.stringify({
                userId: selectedUser.id,
                practices: practicesTrimmedData,
                templateId: values.timelineTemplate,
                startDate: eventDateStart,
                tempo: values.tempo,
                events: [],
            })
        );

        axios
            .post('api/Marketplace/Engagements', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })
            .then((response: any) => {
                if (response) {
                    if (response.status === 200) {
                        createNotification(
                            'Engagement Launched successfully',
                            'success'
                        );
                        props.onClose(response.data);
                    } else if (
                        response.response &&
                        response.response.status === 409
                    ) {
                        createNotification(
                            response.response.data.title,
                            'error'
                        );
                    } else {
                        createNotification(
                            'An unexpected error occurred while creating Engagement.',
                            'error'
                        );
                    }
                }
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while creating Engagement.',
                    'error'
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const getPracticeMultiselectOptions = () => {
        return [...practices].map((x) => {
            return { id: x.id, option: x.name };
        });
    };

    const handlePracticeOptionsChange = (selectedIds: number[]) => {
        setSelectedPracticeIds(selectedIds);

        if (
            selectedIds.length > 0 &&
            !selectedIds.some((x) => x == activeTab)
        ) {
            setActiveTab(selectedIds.find((x) => x != 0));
        }
        setSelectedPractices((prev) => {
            const updatedPractices = selectedIds.map((id) => {
                const existing = prev.find((p) => p.practiceId === id);
                return (
                    existing || {
                        practiceId: id,
                        stateOfRegistration: '',
                        entityType: '',
                        entityName: '',
                    }
                );
            });
            return updatedPractices;
        });
    };

    const renderTagList = ({ value, clearTagIcon }: any) => {
        return (
            <div className="rw-multiselect-taglist">
                {value.map((item: any) => {
                    return (
                        <div className="rw-multiselect-tag" key={item.id}>
                            <span className="rw-multiselect-tag-label">
                                {item.option}
                            </span>

                            <button
                                className="rw-multiselect-tag-btn"
                                onMouseDown={() => {
                                    if (
                                        activeTab === item.id &&
                                        selectedPracticeIds?.length > 0
                                    ) {
                                        setActiveTab(
                                            selectedPracticeIds.find(
                                                (x) => x != 0 && x != item.id
                                            )
                                        );
                                    }
                                    setSelectedPracticeIds((prevState) => {
                                        return prevState.filter(
                                            (x) => x !== item.id
                                        );
                                    });

                                    setSelectedPractices((prevState) => {
                                        return prevState.filter(
                                            (x) => x.practiceId !== item.id
                                        );
                                    });
                                }}
                                aria-label="Remove item">
                                {clearTagIcon}
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderPracticeSelect = () => {
        return (
            <FormGroup className="multiselect">
                <Row>
                    <Col md={3} className="d-flex mt-2">
                        <label className="copy-name-label">
                            Select Practice:
                        </label>
                    </Col>
                    <Col md={9}>
                        <Multiselect
                            open={isPracticeListOpened}
                            onFocus={() => {
                                setIsPracticeListOpened(true);
                            }}
                            onBlur={() => {
                                setIsPracticeListOpened(false);
                            }}
                            filter="contains"
                            selectIcon={<ArrowDown />}
                            tagListComponent={renderTagList}
                            data={getPracticeMultiselectOptions()}
                            value={getPracticeMultiselectOptions().filter((x) =>
                                selectedPracticeIds?.includes(x.id)
                            )}
                            className={
                                isPracticeMultiselectError
                                    ? 'av-invalid is-invalid'
                                    : ''
                            }
                            dataKey="option"
                            placeholder="Select"
                            disabled={practicesAreloading || !selectedUser}
                            textField="option"
                            groupBy="type"
                            onChange={(value) => {
                                const ids = value.map((x) => {
                                    return x.id;
                                });
                                if (ids.length > 0) {
                                    setIsPracticeMultiselectError(false);
                                }
                                handlePracticeOptionsChange(ids);
                                if (
                                    ids.length ===
                                    getPracticeMultiselectOptions().length
                                ) {
                                    setIsPracticeListOpened(false);
                                }
                            }}
                        />
                    </Col>
                </Row>
            </FormGroup>
        );
    };

    const updatePracticeData = (field: string, value: string) => {
        setSelectedPractices((prev) =>
            prev.map((practice) =>
                practice.practiceId === activeTab
                    ? { ...practice, [field]: value }
                    : practice
            )
        );
    };

    const renderTabContent = () => {
        const practice = selectedPractices.find(
            (x) => x.practiceId === activeTab
        );

        let entityNameClass = 'form-control input-height';
        entityNameClass +=
            practiceValidationErrors[activeTab] === true &&
            !practice?.entityName.trim()
                ? ' av-invalid is-invalid'
                : '';

        let entityTypeClass = 'input-height';
        entityTypeClass +=
            practiceValidationErrors[activeTab] === true &&
            !practice?.entityType
                ? ' av-invalid is-invalid'
                : '';

        let stateOfRegistrationClass = 'input-height';
        stateOfRegistrationClass +=
            practiceValidationErrors[activeTab] === true &&
            !practice?.stateOfRegistration
                ? ' av-invalid is-invalid'
                : '';

        return (
            <div className="practice-tab-content">
                <FormGroup>
                    <Row className="mb-10">
                        <Col md={3} className="d-flex mt-2">
                            <label className="copy-name-label" htmlFor="name">
                                Legal Entity Name:
                            </label>
                        </Col>
                        <Col md={9}>
                            <Field
                                className={entityNameClass}
                                value={practice?.entityName || ''}
                                type="text"
                                name={`legalEntityName${practice}`}
                                id={`legalEntityName${practice}`}
                                onChange={(e) =>
                                    updatePracticeData(
                                        'entityName',
                                        e.target.value
                                    )
                                }
                                placeholder="Enter Legal Entity name"
                            />
                        </Col>
                    </Row>
                </FormGroup>
                <FormGroup>
                    <Row className="mb-10">
                        <Col md={3} className="d-flex mt-2">
                            <label className="copy-name-label">
                                Legal Entity Type:
                            </label>
                        </Col>
                        <Col md={9}>
                            <Input
                                className={entityTypeClass}
                                type="select"
                                label="Legal Entity Type:"
                                name={`legalEntityType${practice}`}
                                id="legalEntityType"
                                value={practice?.entityType || ''}
                                onChange={(e) =>
                                    updatePracticeData(
                                        'entityType',
                                        e.target.value
                                    )
                                }
                                placeholder="Select">
                                {[
                                    {
                                        id: '',
                                        name: 'Select',
                                    },
                                    ...LegalEntityTypeList,
                                ].map((item) => (
                                    <option
                                        value={item.id}
                                        key={'LegalEntityType' + item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                </FormGroup>
                <Row className="mb-10">
                    <Col md={3} className="d-flex mt-2">
                        <label className="copy-name-label">
                            Legal Entity State Of Registration
                        </label>
                    </Col>
                    <Col md={9}>
                        <StateSelectField
                            className={stateOfRegistrationClass}
                            name={`stateOfRegistration${practice}`}
                            value={practice?.stateOfRegistration || ''}
                            label=""
                            placeholder="Select"
                            onChange={(e) => {
                                updatePracticeData(
                                    'stateOfRegistration',
                                    e.target.value
                                );
                            }}
                            showLongFormat={true}
                        />
                    </Col>
                </Row>
            </div>
        );
    };

    const renderPracticeTabs = () => {
        return (
            <div>
                <div className="RRT__container mt-10">
                    <div className="RRT__tabs body-tabs">
                        {selectedPracticeIds?.map((item: number, index) => {
                            const practiceOption = practices.find(
                                (x) => x.id === item
                            );
                            const practiceData = selectedPractices.find(
                                (x) => x.practiceId === item
                            );
                            let className =
                                activeTab === item
                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                    : 'RRT__tab';

                            className +=
                                practiceValidationErrors[item] &&
                                (!practiceData.entityType ||
                                    !practiceData.stateOfRegistration ||
                                    !practiceData.entityName)
                                    ? ' mui-error'
                                    : '';

                            return (
                                <div
                                    key={index}
                                    className={className}
                                    onClick={() => {
                                        setActiveTab(item);
                                    }}>
                                    {practiceOption?.name}
                                </div>
                            );
                        })}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        );
    };

    const renderForm = () => (
        <Form
            innerRef={modalRef}
            initialValues={{
                timelineTemplate: undefined,
                engagementLetter: '',
                tempo: undefined,
            }}
            validationSchema={yup.object().shape({
                timelineTemplate: yup
                    .number()
                    .required('Timeline Template is required'),
                engagementLetter: yup
                    .string()
                    .required('Engagement Letter is required'),
                tempo: yup.string().required('Tempo is required'),
            })}
            onSubmit={(values: any) => {
                if (
                    selectedPracticeIds === null ||
                    selectedPracticeIds?.length === 0
                ) {
                    setIsPracticeMultiselectError(true);
                    return;
                }

                const errors: Record<number, boolean> = {};
                selectedPractices.forEach((practice) => {
                    if (
                        !practice.stateOfRegistration ||
                        !practice.entityType ||
                        !practice.entityName.trim()
                    ) {
                        errors[practice.practiceId] = true;
                    }
                });
                setPracticeValidationErrors(errors);

                if (Object.keys(errors).length > 0) {
                    return;
                }

                if (selectedUser) createEngagement(values);
            }}>
            <div className="d-flex flex-column">
                <FormGroup>
                    <Row>
                        <Col md={3} className="d-flex mt-2">
                            <label className="copy-name-label">
                                Select Member:
                            </label>
                        </Col>
                        <Col md={9}>
                            <CustomAutocomplete
                                onTouchEnd={() => setIsUserFieldTouched(true)}
                                isValid={!!selectedUser || !isUserFieldTouched}
                                placeholder="Select"
                                options={userOptions}
                                value={selectedUser}
                                getOptionLabel={(option) => {
                                    return (option as DropdownOption).name;
                                }}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.id}>
                                            {option.name}
                                        </li>
                                    );
                                }}
                                filterOptions={(options, state) => {
                                    if (state.inputValue) {
                                        return options.filter((f) =>
                                            f.name
                                                .toLocaleLowerCase()
                                                .includes(
                                                    state.inputValue.toLocaleLowerCase()
                                                )
                                        );
                                    } else {
                                        return options;
                                    }
                                }}
                                onChange={(event: any, selectedOption: any) => {
                                    setSelectedUser(
                                        userOptions.find(
                                            (u) =>
                                                u.name === selectedOption?.name
                                        )
                                    );
                                }}
                                arrow={
                                    <FontAwesomeIcon
                                        icon={faChevronDown}
                                        className="arrow-down"
                                    />
                                }
                            />
                        </Col>
                    </Row>
                </FormGroup>
                {renderPracticeSelect()}

                <FormGroup>
                    <Row>
                        <Col md={3} className="d-flex mt-2">
                            <label className="copy-name-label">
                                Timeline Template:
                            </label>
                        </Col>
                        <Col md={9}>
                            <Input
                                className="input-height"
                                type="select"
                                label="Timeline Template"
                                name="timelineTemplate"
                                id="timelineTemplate"
                                placeholder="Select">
                                {[
                                    {
                                        id: '',
                                        name: 'Select',
                                        levelOfService: undefined,
                                    },
                                    ...timelineTemplatesTable.timelines,
                                ].map((item) => (
                                    <option
                                        value={item.id}
                                        key={'timelineTemplate' + item.id}>
                                        {item.name}{' '}
                                        {item.levelOfService !== undefined
                                            ? `(${
                                                  LevelOfServiceOptions[
                                                      item.levelOfService as keyof typeof LevelOfServiceOptions
                                                  ]
                                              })`
                                            : ''}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                </FormGroup>

                <FormGroup>
                    <Row>
                        <Col md={3} className="d-flex mt-2">
                            <label className="copy-name-label">
                                Timeline Tempo:
                            </label>
                        </Col>
                        <Col md={9}>
                            <Input
                                className="input-height"
                                type="select"
                                label="Timeline Tempo:"
                                name="tempo"
                                id="tempo"
                                placeholder="Select">
                                {[
                                    {
                                        id: '',
                                        name: 'Select',
                                    },
                                    ...TimelineTempos,
                                ].map((item) => (
                                    <option
                                        value={item.id}
                                        key={'tempo' + item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                </FormGroup>

                <FormGroup>
                    <Row>
                        <Col md={3}>
                            <label>Engagement Start Date:</label>
                        </Col>
                        <Col md={4}>
                            <div className="form-group icon-addon-wrapper">
                                <FormGroup className={'d-flex small-border'}>
                                    <InputGroupAddon
                                        addonType="prepend"
                                        onClick={() => {
                                            StartDateRef.current.setOpen(true);
                                        }}>
                                        <div className="input-group-text">
                                            <FontAwesomeIcon
                                                icon={faCalendarAlt}
                                            />
                                        </div>
                                    </InputGroupAddon>
                                    <DatePicker
                                        portalId="event-start-date-portal"
                                        minDate={new Date()}
                                        ref={StartDateRef}
                                        className={'form-control'}
                                        selected={eventDateStart}
                                        name="startDate"
                                        popperProps={{ strategy: 'fixed' }}
                                        onChange={(value: Date) => {
                                            setEventDateStart(value);
                                        }}
                                        dateFormat="MM/d/yyyy"
                                        customInput={<DateControlInput />}
                                    />
                                </FormGroup>
                            </div>
                        </Col>
                    </Row>
                </FormGroup>

                <FormGroup>
                    <Row>
                        <Col md={3} className="d-flex mt-2">
                            <label
                                className="copy-name-label"
                                htmlFor="engagementLetter">
                                Engagement Letter:
                            </label>
                        </Col>
                        <Col md={9}>
                            <InputGroup>
                                <InputGroupText className="upload-file-group prepend-addon">
                                    <Label
                                        for="engagementLetter-upload-file"
                                        className="choose-file-label">
                                        Choose File
                                    </Label>
                                    <Input
                                        type="file"
                                        name="engagementLetterFile"
                                        id="engagementLetter-upload-file"
                                        accept={fileSelectorExtensions}
                                        className="upload-file-input"
                                        onChange={changeFileHandler}
                                    />
                                </InputGroupText>
                                <Input
                                    type="text"
                                    name="engagementLetter"
                                    disabled={true}
                                    style={{
                                        backgroundColor: '#fff',
                                    }}
                                />
                            </InputGroup>
                        </Col>
                    </Row>
                </FormGroup>

                <FormGroup>
                    {selectedPracticeIds?.length > 0 && renderPracticeTabs()}
                </FormGroup>

                <div className="mt-auto d-flex justify-content-end modal-copy-buttons">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => props.onClose(undefined)}>
                        Cancel
                    </button>
                    <ButtonLoader
                        type="submit"
                        buttonText={'Launch Engagement'}
                        loaderButtonText={''}
                        disabled={isLoading || isSaving}
                        isLoading={isSaving}
                        onClick={() => {
                            setIsUserFieldTouched(true);
                        }}
                        className={
                            isLoading
                                ? 'btn btn-primary loader-submit'
                                : 'btn btn-primary'
                        }
                    />
                </div>
            </div>
        </Form>
    );

    const renderLoader = () => {
        return (
            <div className="loader-container" style={{ height: '10vw' }}>
                <div className="loader-container-inner">
                    <div className="text-center">
                        <Loader active={isLoading} type="ball-scale-multiple" />
                    </div>
                    <h6 className="mt-5">Adding New Project...</h6>
                </div>
            </div>
        );
    };

    return (
        <div>
            <Rodal
                visible={props.isVisible}
                animation={'fade'}
                onClose={() => props.onClose(undefined)}
                showMask={true}
                width={600}
                closeOnEsc
                className="create-timeline-popup"
                center>
                <ModalHeader>Create New Engagement</ModalHeader>
                <ModalBody>
                    {isLoading ? renderLoader() : renderForm()}
                </ModalBody>
            </Rodal>
        </div>
    );
};
